import { type ClassValue, clsx } from "clsx";
import { isEqual } from "lodash";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function useDebounce<T>(value: T, delay: number): { value: T; isDebouncing: boolean } {
	const [debouncedValue, setDebouncedValue] = useState(value);
	const [isDebouncing, setIsDebouncing] = useState(false);
	const previousValueRef = useRef(value);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (isEqual(previousValueRef.current, value)) return;

		setIsDebouncing(true);

		if (timeoutRef.current) clearTimeout(timeoutRef.current);

		timeoutRef.current = setTimeout(() => {
			previousValueRef.current = value;
			setDebouncedValue(value);
			setIsDebouncing(false);
		}, delay);

		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, [value, delay]);

	return { value: debouncedValue, isDebouncing };
}
